<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import Common from '@/libs/common';
import type { Journey } from '@/libs/journey/journeys';
import type { Route } from '@/@types/gtfs-ressource';
import { formatWalkDuration } from './walk-duration';

/** One recommended journey: its duration, arrival time and the walks and lines it chains. */
const props = defineProps<{
  journey: Journey;
  fastest: boolean;
  routes: { [routeId: string]: Route };
}>();

const emit = defineEmits<{ select: [] }>();

const { d } = useI18n();

type Step = { key: string; walk: string } | { key: string; route: Route };

// Walks between quays of a merged stop take no time: nothing to show for them.
const steps = computed<Step[]>(() =>
  props.journey.legs.flatMap((leg, index): Step[] => {
    if (leg.type === 'walk') {
      return leg.duration > 0 ? [{ key: `walk-${index}`, walk: formatWalkDuration(leg) }] : [];
    }
    const route = props.routes[leg.routeId];
    return route ? [{ key: `ride-${index}`, route }] : [];
  }),
);

const duration = computed<string>(() => Common.formatSec(props.journey.arrival - props.journey.departure));
const arrival = computed<string>(() => d(props.journey.arrival * 1000, 'timeShort'));
</script>

<template>
  <button class="journey-card" type="button" @click="emit('select')">
    <span class="journey-card__details">
      <span class="journey-card__headline">
        <span class="journey-card__duration">{{ duration }}</span>
        <span v-if="fastest" class="journey-card__fastest">
          <BaseIcon name="zap" class="journey-card__fastest-icon" />
          {{ $t('itinerary.fastest') }}
        </span>
      </span>
      <span class="journey-card__steps">
        <template v-for="(step, index) in steps" :key="step.key">
          <span v-if="index > 0" class="journey-card__plus" aria-hidden="true">+</span>
          <span v-if="'walk' in step" class="journey-card__walk">
            <BaseIcon name="footprints" class="journey-card__walk-icon" />
            {{ step.walk }}
          </span>
          <RouteBadge v-else :route="step.route" size="small" />
        </template>
      </span>
    </span>
    <span class="journey-card__arrival">{{ arrival }}</span>
  </button>
</template>

<style lang="scss">
.journey-card {
  align-items: center;
  background-color: $color-white;
  border: 1px solid $color-border-medium;
  border-radius: $radius-xl;
  color: $color-neutral-highest;
  cursor: pointer;
  display: flex;
  gap: $spacing-4;
  justify-content: space-between;
  padding: $spacing-4;
  text-align: left;
  width: 100%;

  &:active {
    background-color: $color-neutral-lower;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: $spacing-2;
    min-width: 0;
  }

  &__headline {
    align-items: center;
    display: flex;
    gap: 6px;
  }

  &__duration {
    font: $text-bold-large;
    letter-spacing: $text-bold-large-spacing;
    white-space: nowrap;
  }

  &__fastest {
    align-items: center;
    background-color: $color-primary-brand-medium;
    border-radius: $radius-tag;
    color: $color-primary-brand-lowest;
    display: inline-flex;
    font: $label-medium;
    gap: $spacing-1;
    height: 24px;
    line-height: 1;
    padding: $spacing-1 6px $spacing-1 $spacing-1;
    white-space: nowrap;
  }

  &__fastest-icon {
    font-size: 16px;
  }

  &__steps {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
  }

  &__walk {
    align-items: center;
    color: $color-text-neutral;
    display: inline-flex;
    font: $text-smaller;
    gap: $spacing-1;
    white-space: nowrap;
  }

  &__walk-icon {
    font-size: 14px;
  }

  &__plus {
    color: $color-neutral-higher;
    font: $text-small;
  }

  &__arrival {
    flex-shrink: 0;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }
}
</style>
